import type { SnapshotMetaItemsModel } from '../models/snapshot-meta-items-model';
import type { SnapshotStorage } from '../snapshot-storage';
import type { BackgroundTask } from '../definitions/background-task';

// 所有快照的最大占用空间，单位是 Bytes
const MB = 1e6;
const HARD_SIZE_LIMIT = 20 * MB;
const SOFT_SIZE_LIMIT = HARD_SIZE_LIMIT * 0.8;

// 自动销毁的最大间隔时间，单位：毫秒
const AUTO_EVICT_MAX_INTERVAL = 15 * 60 * 1000;

// 至少维持的快照数量（推荐值）
const CLEANUP_MIN_REMAIN_COUNT = 12;

enum AutoEvictStatus {
    Started = 'started',
    Evicting = 'evicting',
    Stopped = 'stopped',
}

export class AutoEvictTask implements BackgroundTask {
    private status: AutoEvictStatus;

    private timer: ReturnType<typeof setTimeout>;

    constructor(
        private storage: SnapshotStorage,
        private logger: Pick<typeof console, 'debug' | 'error' | 'info' | 'log' | 'warn'>,
    ) {}

    public async perform() {
        this.status = AutoEvictStatus.Evicting;
        const snapshots = await this.storage.list();
        const statistics = this.storage.statistics(snapshots);
        const candidates: SnapshotMetaItemsModel[] = [];

        snapshots.forEach((snapshot) => {
            if (statistics.count < CLEANUP_MIN_REMAIN_COUNT || statistics.totalSizeInBytes < SOFT_SIZE_LIMIT) {
                return;
            }

            candidates.push(snapshot);
            statistics.totalSizeInBytes -= snapshot.size;
            statistics.count -= 1;
        });

        await this.storage.remove(...candidates.map((c) => c.id));
        if (this.status === AutoEvictStatus.Evicting) {
            this.status = AutoEvictStatus.Started;
        }
    }

    public start() {
        this.status = AutoEvictStatus.Started;
        this.loop();
    }

    public stop() {
        clearTimeout(this.timer);
        this.status = AutoEvictStatus.Stopped;
    }

    private loop = () => {
        if (this.status === AutoEvictStatus.Stopped) {
            return;
        }

        clearTimeout(this.timer);
        this.timer = setTimeout(() => {
            this.evictIfNeeded(this.loop);
        }, AUTO_EVICT_MAX_INTERVAL);
    };

    private async needEvict(): Promise<boolean> {
        if (this.status === AutoEvictStatus.Stopped) {
            return false;
        }

        const snapshots = await this.storage.list();
        const statistics = this.storage.statistics(snapshots);

        return statistics.totalSizeInBytes > SOFT_SIZE_LIMIT;
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    private evictIfNeeded = async (onFinish = () => {}) => {
        try {
            if (await this.needEvict()) {
                await this.perform();
            }
        } catch (error) {
            this.logger.error(error);
        }

        onFinish();
    };
}
